<script setup lang="ts">
import { useFuse } from '@vueuse/integrations/useFuse.mjs'
import { CheckboxBlankOutlineIcon, CheckboxMarkedIcon } from 'mdi-vue3'
import { computed } from 'vue'

import AComboboxItem from '../ACombobox/AComboboxItem.vue'
import AMultiSelectRoot, { type AMultiSelectRootProperties } from './AMultiSelectRoot.vue'
import AMultiSelectTags from './AMultiSelectTags.vue'

export interface AMultiStringSelectProperties extends AMultiSelectRootProperties {
  items: string[]
  placeholder?: string
  disabled?: boolean
  arrayType?: 'string'

  /**
   * Just here to prevent it from passing through to the root component.
   */
  multiple: true

  /**
   * Enable or disable the default search functionality.
   * If set to true, the search input will not filter the items.
   */
  ignoreSearch?: boolean
}

const properties = withDefaults(defineProps<AMultiStringSelectProperties>(), {
  placeholder: undefined,
  arrayType: 'string',
  disabled: false,
})

const modelValue = defineModel<string[]>('modelValue', { required: false, default: [] })

const search = defineModel<string>('search', { required: false, default: undefined })

const { results: fuzzyResults } = useFuse(search, properties.items)

const filteredItems = computed((): string[] => {
  if (search.value.length === 0 || properties.ignoreSearch) return properties.items
  return fuzzyResults.value.map((result) => result.item)
})

function deleteIndex(index: number): void {
  if (index === -1) return
  modelValue.value?.splice(index, 1)
  // Reset the modelValue to trigger reactivity
  modelValue.value = [...(modelValue.value || [])]
}
</script>

<template>
  <AMultiSelectRoot v-model="modelValue" v-model:search="search" :placeholder>
    <template #tags="contextProperties">
      <AMultiSelectTags :max-count :model-value="modelValue" @delete:index="deleteIndex">
        <template #item="{ item, index }">
          <slot name="item" v-bind="{ item, index, contextProperties }">
            {{ item }}
          </slot>
        </template>
      </AMultiSelectTags>
    </template>

    <template #items="contextProperties">
      <AComboboxItem
        v-for="(item, index) in filteredItems"
        :key="item"
        class="atabix-multiselect-item"
        :item-id="`${contextProperties.uniqueSelectId}-${item}`"
        :value="item"
      >
        <CheckboxMarkedIcon v-if="modelValue.includes(item)" class="size-4" />
        <CheckboxBlankOutlineIcon v-else class="size-4" />

        <slot name="item" v-bind="{ item, index, contextProperties }">
          {{ item }}
        </slot>
      </AComboboxItem>
    </template>

    <template #empty><slot name="empty" /></template>
  </AMultiSelectRoot>
</template>

<style lang="scss"></style>
